"use client";

import type { ReactNode } from "react";

import { useLayoutEffect, useRef, useState } from "react";
import waterTowerBase64 from "../assets/sj-sound-familiar/water-tower.svg";

const waterTowerImage = `data:image/svg+xml;base64,${waterTowerBase64}`;

/**
 * Full desktop-final frame. The 152px side gutters live inside this width
 * so they scale with the composition instead of staying fixed.
 */
const DESIGN_WIDTH = 1440;

const DESKTOP_MEDIA_QUERY = "(min-width: 768px)";

/**
 * Locks children to a fixed artboard width and uniformly scales them down
 * to fit the container — composition stays together like a scaled image.
 * Does not scale up past 1. Only applies at `md` and above; smaller
 * viewports stay fluid.
 *
 * Desktop artboard classes are applied only after measure (`isDesktop`),
 * not via Tailwind `md:` — otherwise CSS forces 1440px before `zoom` and
 * `overflow-x-clip` hides the right column until hydration.
 *
 * Uses `zoom` (not transform) so the layout box shrinks with the content —
 * no zero-height clip before measure, and no manual height sync.
 */
function ScaleToFit({
  children,
  designWidth,
}: {
  children: (state: { isDesktop: boolean }) => ReactNode;
  designWidth: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [isDesktop, setIsDesktop] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY);

    const update = () => {
      const matches = mediaQueryList.matches;
      setIsDesktop(matches);
      setScale(matches ? Math.min(1, container.clientWidth / designWidth) : 1);
    };

    const observer = new ResizeObserver(update);
    observer.observe(container);
    mediaQueryList.addEventListener("change", update);
    update();

    return () => {
      observer.disconnect();
      mediaQueryList.removeEventListener("change", update);
    };
  }, [designWidth]);

  return (
    <div className="w-full overflow-x-clip" ref={containerRef}>
      <div style={isDesktop ? { width: designWidth, zoom: scale } : undefined}>
        {children({ isDesktop })}
      </div>
    </div>
  );
}

// Mobile 7653:2517 (stacked: eyebrow → headline → tower → quote → copy).
// Desktop 6595:2620 + tower 6595:2607 + quote 6595:2581, locked inside the
// 1440 frame and scaled via ScaleToFit from 768px up. The tower is
// grid self-end in the left column so its stem always rests on the banner;
// the 416px left column (360 + 56) puts the tower top 11px below the row.
function SjSoundFamiliar() {
  return (
    <section
      aria-labelledby="sj-sound-familiar-heading"
      className="bg-campaign-shell font-jakarta text-new-beginnings-base"
    >
      <div className="mx-auto w-full max-w-[1440px] px-4 pt-[52px] pb-8 md:px-0 md:py-0">
        <ScaleToFit designWidth={DESIGN_WIDTH}>
          {({ isDesktop }) => (
            <div
              className={isDesktop ? "w-[1440px] px-[152px] pt-20" : undefined}
            >
              <div
                className={
                  isDesktop
                    ? "grid w-[1136px] grid-cols-[504px_560px] gap-x-[72px]"
                    : "flex flex-col"
                }
              >
                <p
                  className={
                    isDesktop
                      ? "col-span-2 mb-4 text-base font-extrabold uppercase"
                      : "text-base font-extrabold uppercase"
                  }
                >
                  Sound familiar?
                </p>

                <h2
                  className={
                    isDesktop
                      ? "relative z-10 col-start-1 row-start-2 text-2xl font-bold"
                      : "relative z-10 mt-4 text-2xl font-bold"
                  }
                  id="sj-sound-familiar-heading"
                >
                  Somehow, it always
                  {isDesktop ? <br /> : " "}
                  falls to a Jeanie.
                </h2>

                {/* Base64 package asset — next/image cannot optimize data URIs. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  alt="Save Jeanie"
                  className={
                    isDesktop
                      ? "col-start-1 row-span-2 row-start-2 ml-[52px] h-auto w-[288px] self-end"
                      : "mx-auto mt-8 h-auto w-[288px]"
                  }
                  height="405"
                  src={waterTowerImage}
                  width="288"
                />

                <blockquote
                  className={
                    isDesktop
                      ? "relative z-10 col-span-2 row-start-4 flex h-[104px] items-center justify-center rounded-[8px] bg-[#C42D20] text-center text-2xl leading-[72px] font-bold text-white"
                      : "relative z-10 -mt-[5px] flex min-h-[128px] items-center justify-center rounded-[8px] bg-[#C42D20] px-[18px] py-4 text-center text-xl font-bold text-white"
                  }
                >
                  “Because if I stop, nothing gets done.”
                </blockquote>

                <div
                  className={
                    isDesktop
                      ? "col-start-2 row-span-2 row-start-2 min-h-[416px] pb-14"
                      : "mt-8"
                  }
                >
                  <p
                    className={
                      isDesktop
                        ? "min-h-[72px] text-lg font-bold"
                        : "text-lg font-bold"
                    }
                  >
                    Forty years later, Ferris Bueller’s sister Jeanie is still
                    the responsible sibling.
                  </p>
                  <div
                    className={
                      isDesktop
                        ? "text-md mt-4 space-y-8 leading-8 font-medium"
                        : "text-md mt-8 space-y-8 leading-8 font-medium"
                    }
                  >
                    <p>
                      Only now, she isn’t chasing Ferris around Chicago. She’s
                      keeping track of Mom and Dad’s appointments. Picking up
                      prescriptions. Handling groceries. Checking in. Fixing
                      whatever needs fixing. And wondering why her brother
                      always seems to be somewhere else.
                    </p>
                    <p>
                      For a lot of families, that dynamic is very real. One
                      person gradually becomes the one everyone counts on.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </ScaleToFit>
      </div>
    </section>
  );
}

export { SjSoundFamiliar };
